import { createApiRef } from '@backstage/core-plugin-api';
import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type CatalogAnnotationsResolveResponse = {
  parsedRepo: {
    kind: string;
    host?: string;
    catalogInfoPath?: string;
    defaultBranch?: string;
  };
  /** catalog-info.yaml from the default branch (or entity-derived baseline if missing) before merges */
  baseYaml: string;
  previewYaml: string;
  suggestions: {
    snyk?: {
      orgId?: string;
      suggestedTargetId?: string;
      targetResolutionNote?: string;
    };
    pagerduty: {
      services: Array<{ id: string; name: string; htmlUrl?: string }>;
    };
    jira: { defaultComponent: string };
  };
  targetResolutionNote?: string;
};

export type CatalogAnnotationsApplyResponse = {
  url: string;
  number?: number;
};

export interface CatalogAnnotationsApi {
  resolve(input: {
    entityRef: string;
    selectedKeys: string[];
    annotationOverrides?: Record<string, string>;
  }): Promise<CatalogAnnotationsResolveResponse>;

  apply(input: {
    entityRef: string;
    branchName?: string;
    annotations: Record<string, string>;
  }): Promise<CatalogAnnotationsApplyResponse>;
}

export const catalogAnnotationsApiRef = createApiRef<CatalogAnnotationsApi>({
  id: 'plugin.catalog-annotations.service',
});

export class CatalogAnnotationsClient implements CatalogAnnotationsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async request<T>(path: string, body: unknown): Promise<T> {
    const raw = await this.discoveryApi.getBaseUrl('catalog-annotations');
    const baseUrl = raw.replace(/\/$/, '');
    const res = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return (await res.json()) as T;
  }

  async resolve(input: {
    entityRef: string;
    selectedKeys: string[];
    annotationOverrides?: Record<string, string>;
  }): Promise<CatalogAnnotationsResolveResponse> {
    return this.request<CatalogAnnotationsResolveResponse>('/resolve', input);
  }

  async apply(input: {
    entityRef: string;
    branchName?: string;
    annotations: Record<string, string>;
  }): Promise<CatalogAnnotationsApplyResponse> {
    return this.request<CatalogAnnotationsApplyResponse>('/apply', input);
  }
}
